import { useEffect } from 'react';
import { onInstant } from '@/shared/api/websocket/realtime-sync.registry';
import {
  payloadObject,
  payloadString,
} from '@/shared/api/websocket/realtime-sync.payload';
import { useUser } from '@/app/model/stores';
import { isAngleValidationFinished } from '@/shared/constants/status';
import type { ValidationItem } from './types';

interface UseValidationListenerProps {
  setValidationItems: React.Dispatch<React.SetStateAction<ValidationItem[]>>;
  notSuitableImageMessage: string;
}

export function useValidationListener({
  setValidationItems,
  notSuitableImageMessage,
}: UseValidationListenerProps) {
  const user = useUser();

  useEffect(() => {
    if (!user) return;

    const cleanup = onInstant('angles', (event) => {
      if (event.action !== 'validation_completed') return;
      if (!event.id) return;

      const validation = payloadObject(event.payload, 'validation');
      if (!validation) return;

      const status = payloadString(validation, 'status');
      if (!status || !isAngleValidationFinished(status)) return;

      const isValid = status === 'valid';
      const isTechnicalError = status === 'technical_error';
      const isProhibited = status === 'prohibited';
      const recommendations = payloadString(validation, 'recommendations');

      setValidationItems((prev) =>
        prev.map((item) => {
          if (item.id !== event.id) return item;
          return {
            ...item,
            status: (isValid ? 'valid' : 'invalid') as ValidationItem['status'],
            validation: {
              is_valid: isValid,
              is_prohibited: isProhibited,
              is_technical_error: isTechnicalError,
              recommendations: recommendations ?? undefined,
            },
            error: !isValid
              ? recommendations || notSuitableImageMessage
              : undefined,
          };
        })
      );
    });

    return cleanup;
  }, [user, notSuitableImageMessage, setValidationItems]);
}
